// @flow

import React, { type Node } from 'react';
import styled from 'styled-components';
import { useUniqueId } from '@tectonics/hooks';
import { useUserInputAsTextFieldWithFormatting } from '@tectonics/prs/user-input-values';
import { useUserInputValidationError, type UserInputValidationError, serverErrorMessage } from '@tectonics/prs/validation';
import { useSubmitting } from '@tectonics/prs/submitting';
import { getZipcodeNormalizer } from '@tectonics/prs/customer/normalize';

import { TextField } from '../../../../atoms';
import { ErrorLabel } from '../../../../molecules/error-label';
import { useFormHasBeenSent } from '@tectonics/prs/form-status';

import { InputMask, formatValueWithMask } from '@tectonics/components/input-mask';
import { useCurrentPaymentTypeOptionalFields } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';

const zipcodeNormalizer = getZipcodeNormalizer('ar');

export function FieldArgentineZipcode(): Node {
  const inputId = useUniqueId();
  const submitting = useSubmitting();

  const [value, handleChange] = useUserInputAsTextFieldWithFormatting(
    'argentineZipcode',
    formatRaw,
    zipcodeNormalizer,
  );

  const validationError = useUserInputValidationError('argentineZipcode');
  const formHasBeenSent = useFormHasBeenSent();

  const isOptionalFieldByPaymentType = useCurrentPaymentTypeOptionalFields('zipcode');

  if (isOptionalFieldByPaymentType) {
    return null;
  }

  return (
    <div data-test-name="address-zipcode">
      <LabelWrapper>
        <label htmlFor={inputId}>
          Código Postal
        </label>
      </LabelWrapper>

      <TextField
        as={InputMask}
        id={inputId}
        type="tel"
        mask={ZIPCODE_MASK}
        value={value}
        onChange={handleChange}
        fashion={formHasBeenSent && validationError ? 'error' : null}
        readOnly={submitting}
        data-error={formHasBeenSent && validationError ? 'error' : null}
        name="address-zipcode"
      />

      {formHasBeenSent && validationError && (
        <ValidationMessage validationError={validationError} />
      )}
    </div>
  );
}

export const LabelWrapper = styled.div`
  display: flex;
  label {
    flex-grow: 1;
  }
`;

type ValidationMessageProps = {|
  validationError: UserInputValidationError,
|};

function ValidationMessage({ validationError }: ValidationMessageProps): Node {
  switch (validationError.id) {
    case 'empty':
      return (
        <ErrorLabel>
          Informa tu Código Postal.
        </ErrorLabel>
      );

    case 'server-error':
      return (
        <ErrorLabel>
          {serverErrorMessage(validationError.payload)}
        </ErrorLabel>
      );

    default:
      return (
        <ErrorLabel>
          El Código Postal que informaste no es válido
        </ErrorLabel>
      );
  }
}

const ZIPCODE_MASK = [
  /[0-9]/, /[0-9]/, /[0-9]/, /[0-9]/,
];

function formatRaw(rawValue: string): string {
  return formatValueWithMask(rawValue, ZIPCODE_MASK);
}
